import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // ✅ Added scrollTop function
  const scrollTop = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="privacy-policy-container">
      {/* Header Section */}
      <div className="privacy-policy-header">
        <h1 className="privacy-policy-title">Privacy Policy</h1>
        <div className="breadcrumb">
          <button className="breadcrumb-home" onClick={() => scrollTop('/')}>
            Home
          </button>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Privacy Policy</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="privacy-policy-content">
        <div className="privacy-policy-text">
          <p className="last-updated">
            <strong>Last Updated:</strong> December 2024
          </p>
          
          <p>
            At <strong>HotelPlacement.ae</strong>, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our hospitality recruitment platform.
          </p>

          <h2 className="section-title">Information We Collect</h2>
          
          <h3 className="subsection-title">Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul className="policy-list">
            <li>Name, email address, and contact information</li>
            <li>Professional background and work experience</li>
            <li>Educational qualifications and certifications</li>
            <li>Resume and cover letter documents</li>
            <li>Profile photographs</li>
            <li>Location information</li>
            <li>Account credentials and preferences</li>
          </ul>

          <h3 className="subsection-title">Technical Information</h3>
          <p>We automatically collect certain technical information when you use our platform:</p>
          <ul className="policy-list">
            <li>IP address and device information</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Pages visited and time spent on our platform</li>
            <li>Referring website information</li>
          </ul>

          <h2 className="section-title">How We Use Your Information</h2>
          
          <p>We use your personal information for the following purposes:</p>
          <ul className="policy-list">
            <li>To provide and maintain our recruitment services</li>
            <li>To match job seekers with relevant employment opportunities</li>
            <li>To facilitate communication between employers and candidates</li>
            <li>To improve our platform and develop new features</li>
            <li>To send important updates and notifications</li>
            <li>To comply with legal obligations and enforce our terms</li>
            <li>To prevent fraud and ensure platform security</li>
          </ul>

          <h2 className="section-title">Information Sharing and Disclosure</h2>
          
          <p>We may share your information in the following circumstances:</p>
          <ul className="policy-list">
            <li><strong>With Employers:</strong> When you apply for jobs, we share your profile information with potential employers</li>
            <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our platform</li>
            <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights and safety</li>
            <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets, your information may be transferred</li>
            <li><strong>With Consent:</strong> We may share information with your explicit consent</li>
          </ul>

          <h2 className="section-title">Data Security</h2>
          
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
          </p>

          <h2 className="section-title">Your Rights and Choices</h2>
          
          <p>You have the following rights regarding your personal information:</p>
          <ul className="policy-list">
            <li><strong>Access:</strong> Request access to your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Account Closure:</strong> Close your account at any time</li>
          </ul>

          <h2 className="section-title">Cookies and Tracking Technologies</h2>
          
          <p>
            We use cookies and similar tracking technologies to enhance your experience on our platform. Cookies help us remember your preferences, analyze platform usage, and provide personalized content. You can control cookie settings through your browser preferences.
          </p>

          <h2 className="section-title">Third-Party Links</h2>
          
          <p>
            Our platform may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
          </p>

          <h2 className="section-title">International Data Transfers</h2>
          
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information during such transfers.
          </p>

          <h2 className="section-title">Children's Privacy</h2>
          
          <p>
            Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected such information, we will take steps to delete it promptly.
          </p>

          <h2 className="section-title">Changes to This Privacy Policy</h2>
          
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our platform after such changes constitutes acceptance of the updated policy.
          </p>

          <h2 className="section-title">Contact Us</h2>
          
          <p>
            If you have any questions about this Privacy Policy or our privacy practices, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> info@hotelplacement.ae</p>
            <p><strong>Phone:</strong> 971-42503121</p>
            <p><strong>Address:</strong> Aspin Towers, Sheikh Zayed Road, Dubai, UAE</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="privacy-policy-footer">
        <div className="footer-content">
          <div className="footer-column">
            <h3>Contact Us</h3>
            <div className="contact-item">
              <span className="contact-icon">📍</span>
              <span>Aspin Towers, Sheikh Zayed Road, Dubai, UAE</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📱</span>
              <span>971-42503121</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">✉️</span>
              <span>info@hotelplacement.ae</span>
            </div>
          </div>
          <div className="footer-column">
            <h3>Company & Policy</h3>
            <div className="policy-links">
              <button className="policy-link" onClick={() => scrollTop('/AboutUs')}>About Us</button>
              <button className="policy-link" onClick={() => scrollTop('/')}>Home</button>
              <button className="policy-link" onClick={() => scrollTop('/faq')}>FAQs</button>
              <button className="policy-link active">Privacy Policy</button>
              <button className="policy-link" onClick={() => scrollTop('/terms-conditions')}>Terms & Condition</button>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p>&copy; 2024 HotelPlacement.ae. All rights reserved.</p>
            <div className="footer-social">
              <span>Follow us:</span>
              {/* ✅ FIXED: Added valid hrefs and security attributes */}
              <a 
                href="https://www.linkedin.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              <a 
                href="https://twitter.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Twitter
              </a>
              <a 
                href="https://www.facebook.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;